import { makeAutoObservable, reaction, IReactionDisposer } from "mobx";
import shortid from "shortid";
import { logger } from "library/core/utility";
import CookieStorage, { CookieOptions } from "library/core/utility/cookie-storage";
import { layoutStore } from "library/core/stores/layout/LayoutStore";
import {
  profileStore,
  authStore,
  broadcastStore,
  nodeChatStore,
} from "core/stores";
import { FEATURE_FLAGS } from "core/stores/auth/feature-flags";
import { IChatMessage } from "common/broadcast/_stores/chat/interfaces";
import { ChatModeEnum } from "common/broadcast/_stores/chat/enums";
import { IShowType } from "common/broadcast/_stores/broadcast/enums";
import { aiChatService } from "./AiChatService";
import { AiChatRequest, AiRecommendation, AiChatPosition } from "./types";
import {
  InitialData,
  AI_CHAT_FREE_SHOW_ONLY,
  AI_CHAT_COOKIE_ENABLED,
  AI_CHAT_COOKIE_POSITION,
} from "./consts";

const logPrefix = "[AiChatStore]:";
const MAX_RECOMMENDATIONS = 3;
const AUTO_DISMISS_MS = 60000;
const QUIET_ROOM_MS = 120000;
const ENGAGE_INTERVAL_MS = 300000;
const REPLACE_THRESHOLD_MS = 2000;
const MAX_HISTORY_MESSAGES = 30;
const HISTORY_STORAGE_KEY = "ai_chat_message_history";
const HISTORY_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface QueueItem {
  type: "member" | "system";
  message?: IChatMessage;
  systemMessage?: string;
  sessionId?: string;
  targetUsername?: string;
  chatType?: "global" | "member";
  targetUserId?: string;
  memberHandle?: string;
}

export default class AiChatStore {
  aiRecommendations: AiRecommendation[] = InitialData.aiRecommendations;
  pendingQueue: QueueItem[] = [];
  isProcessingQueue: boolean = InitialData.isProcessingQueue;
  public isUserEnabled: boolean = true;
  public recommendationPosition: AiChatPosition = "auto";

  private dismissTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private messageReactionDisposer: IReactionDisposer | null = null;
  private memberJoinDisposer: IReactionDisposer | null = null;
  private quietRoomTimer: ReturnType<typeof setTimeout> | null = null;
  private engageInterval: ReturnType<typeof setInterval> | null = null;
  private lastSeenPublicCount: number = 0;
  private lastSeenPrivateCount: number = 0;
  private privateMessageReactionDisposer: IReactionDisposer | null = null;
  private showTypeChangeDisposer: IReactionDisposer | null = null;
  private memberJoinSuppressed: boolean = false;
  private memberJoinSuppressTimer: ReturnType<typeof setTimeout> | null = null;
  private memberLeaveDisposer: IReactionDisposer | null = null;
  private transactionDisposer: IReactionDisposer | null = null;
  private favoriteDisposer: IReactionDisposer | null = null;
  private memberStatusDisposer: IReactionDisposer | null = null;
  private transactionTriggerUsernames: Set<string> = new Set();
  private messageHistory: Map<string, string[]> = new Map();
  private aiSentMessages: Set<string> = new Set();

  constructor() {
    makeAutoObservable(this);
    this.loadSettings();
    this.loadMessageHistory();
  }

  log = (...params: any[]) => {
    logger.log("info", logPrefix, ...params);
  };

  /**
   * Whether the AI chat feature is active (feature flag enabled AND user has not disabled it).
   * Controls whether reactions run and the feature is operational.
   */
  get isEffectivelyEnabled(): boolean {
    if (!authStore.isFeatureEnabled(FEATURE_FLAGS.FEATURE_AI_CHAT_MESSAGES)) {
      return false;
    }
    return this.isUserEnabled;
  }

  /**
   * Whether AI chat UI elements (sparkle icons, recommendation cards) should be visible.
   * Combines feature activation with show-type restrictions.
   */
  get isUiVisible(): boolean {
    return this.isEffectivelyEnabled && this.isAllowedForCurrentShow;
  }

  /**
   * Resolved position for recommendation cards. "auto" uses responsive logic
   * (top on small/tablet/mobile, bottom on large screens). Explicit "top" or
   * "bottom" forces that position regardless of window size.
   */
  get effectivePosition(): "top" | "bottom" {
    if (this.recommendationPosition === "top") return "top";
    if (this.recommendationPosition === "bottom") return "bottom";
    if (layoutStore.isTabletScreenSize || layoutStore.isMobileDevice) {
      return "top";
    }
    return "bottom";
  }

  private static readonly COOKIE_OPTIONS: CookieOptions = {
    sameSite: "none",
    secure: true,
    expires: 365,
  };

  private loadSettings = () => {
    const enabledCookie = CookieStorage.get(AI_CHAT_COOKIE_ENABLED, false);
    if (enabledCookie !== null && enabledCookie !== undefined) {
      this.isUserEnabled = enabledCookie !== "false";
    }
    const positionCookie = CookieStorage.get(AI_CHAT_COOKIE_POSITION, false);
    if (positionCookie === "auto" || positionCookie === "top" || positionCookie === "bottom") {
      this.recommendationPosition = positionCookie;
    }
  };

  public setIsUserEnabled = (enabled: boolean) => {
    this.isUserEnabled = enabled;
    CookieStorage.set(
      AI_CHAT_COOKIE_ENABLED,
      String(enabled),
      AiChatStore.COOKIE_OPTIONS
    );
  };

  public setRecommendationPosition = (position: AiChatPosition) => {
    this.recommendationPosition = position;
    CookieStorage.set(
      AI_CHAT_COOKIE_POSITION,
      position,
      AiChatStore.COOKIE_OPTIONS
    );
  };

  setupReaction = () => {
    this.lastSeenPublicCount = nodeChatStore.publicChatMessages.length;
    this.messageReactionDisposer = reaction(
      () => nodeChatStore.publicChatMessages.length,
      newLength => {
        if (newLength > this.lastSeenPublicCount) {
          const newMessages = nodeChatStore.publicChatMessages.slice(
            this.lastSeenPublicCount
          );
          for (const msg of newMessages) {
            if (msg.isModel && msg.text) {
              if (!this.aiSentMessages.delete(msg.text)) {
                this.recordMessageHistory("model", msg.text);
              }
            } else {
              this.onNewMemberMessage(msg);
            }
          }
        }
        this.lastSeenPublicCount = newLength;
      }
    );

    this.lastSeenPrivateCount = nodeChatStore.privateChatMessages.length;
    this.privateMessageReactionDisposer = reaction(
      () => nodeChatStore.privateChatMessages.length,
      newLength => {
        if (newLength > this.lastSeenPrivateCount) {
          const newMessages = nodeChatStore.privateChatMessages.slice(
            this.lastSeenPrivateCount
          );
          for (const msg of newMessages) {
            if (msg.isModel && msg.text) {
              if (!this.aiSentMessages.delete(msg.text)) {
                this.recordMessageHistory("model", msg.text);
              }
            } else {
              this.onNewMemberMessage(msg);
            }
          }
        }
        this.lastSeenPrivateCount = newLength;
      }
    );

    this.setupSystemTriggers();
  };

  resetStore = () => {
    this.dismissTimers.forEach(timer => clearTimeout(timer));
    this.dismissTimers.clear();
    if (this.messageReactionDisposer) {
      this.messageReactionDisposer();
      this.messageReactionDisposer = null;
    }
    if (this.privateMessageReactionDisposer) {
      this.privateMessageReactionDisposer();
      this.privateMessageReactionDisposer = null;
    }
    if (this.memberJoinDisposer) {
      this.memberJoinDisposer();
      this.memberJoinDisposer = null;
    }
    if (this.quietRoomTimer) {
      clearTimeout(this.quietRoomTimer);
      this.quietRoomTimer = null;
    }
    if (this.engageInterval) {
      clearInterval(this.engageInterval);
      this.engageInterval = null;
    }
    if (this.memberLeaveDisposer) {
      this.memberLeaveDisposer();
      this.memberLeaveDisposer = null;
    }
    if (this.showTypeChangeDisposer) {
      this.showTypeChangeDisposer();
      this.showTypeChangeDisposer = null;
    }
    if (this.memberJoinSuppressTimer) {
      clearTimeout(this.memberJoinSuppressTimer);
      this.memberJoinSuppressTimer = null;
    }
    if (this.transactionDisposer) {
      this.transactionDisposer();
      this.transactionDisposer = null;
    }
    if (this.favoriteDisposer) {
      this.favoriteDisposer();
      this.favoriteDisposer = null;
    }
    if (this.memberStatusDisposer) {
      this.memberStatusDisposer();
      this.memberStatusDisposer = null;
    }
    this.memberJoinSuppressed = false;
    this.transactionTriggerUsernames.clear();
    Object.assign(this, {
      aiRecommendations: [],
      pendingQueue: [],
      isProcessingQueue: false,
    });
    this.lastSeenPublicCount = 0;
    this.lastSeenPrivateCount = 0;
  };

  get isDraftingReplies(): boolean {
    return this.isProcessingQueue || this.pendingQueue.length > 0;
  }

  private get isAllowedForCurrentShow(): boolean {
    if (!AI_CHAT_FREE_SHOW_ONLY) return true;
    return broadcastStore.currentShowType === IShowType.FREE;
  }

  onNewMemberMessage = (message: IChatMessage) => {
    if (message.isModel || message.isAdmin) return;

    this.resetQuietRoomTimer();

    const cleanUsername = this.cleanMemberUsername(message.from);
    const existingCard = this.aiRecommendations.find(
      r => !r.isSystemTrigger && r.targetUsername === cleanUsername
    );
    if (existingCard) {
      const age = Date.now() - existingCard.createdAt;
      if (age < REPLACE_THRESHOLD_MS) return;
    }

    this.pendingQueue = [...this.pendingQueue, { type: "member", message }];
    this.processQueue();
  };

  processQueue = async () => {
    if (this.isProcessingQueue || this.pendingQueue.length === 0) return;

    this.isProcessingQueue = true;
    const item = this.pendingQueue[0];
    this.pendingQueue = this.pendingQueue.slice(1);

    try {
      let request: AiChatRequest;
      let isSystemTrigger = false;

      if (item.type === "system" && item.systemMessage) {
        request = this.buildSystemPayload(
          item.systemMessage,
          item.sessionId,
          item.chatType
        );
        isSystemTrigger = true;
      } else if (item.type === "member" && item.message) {
        request = this.buildRequestPayload(item.message);
      } else {
        return;
      }

      const memberHandle = item.memberHandle || item.message?.from;
      if (memberHandle) {
        const member = nodeChatStore.allMemberHash[memberHandle.toLowerCase()];
        if (member) {
          const tier = member.spendingTier || member.sort || 0;
          request.top_spender =
            !!member.isHighValue ||
            !!member.topAdmirer ||
            tier >= 0.8;
        }
      }

      const modelMessages = this.messageHistory.get("model") || [];
      if (modelMessages.length > 0) {
        request.model_messages = [...modelMessages];
      }

      const response = await aiChatService.generateReply(request);
      const cleanedReply = this.stripRoleplayText(response.body);

      if (cleanedReply !== "" && this.isEffectivelyEnabled && this.isAllowedForCurrentShow) {
        const id = shortid.generate();

        const isPrivateSystemTrigger =
          isSystemTrigger &&
          item.chatType === "member" &&
          !!item.targetUsername;

        const recommendation: AiRecommendation = isPrivateSystemTrigger
          ? {
              id,
              targetUsername: item.targetUsername!,
              targetUserId: item.targetUserId,
              isWhisper: broadcastStore.currentShowType !== IShowType.PRIVATE,
              isSystemTrigger: false,
              message: null,
              suggestedReply: cleanedReply,
              createdAt: Date.now(),
            }
          : isSystemTrigger
          ? {
              id,
              targetUsername: item.targetUsername || "",
              isWhisper: false,
              isSystemTrigger: true,
              message: null,
              suggestedReply: cleanedReply,
              createdAt: Date.now(),
            }
          : {
              id,
              targetUsername: this.cleanMemberUsername(item.message!.from),
              targetUserId: item.message!.userId,
              isWhisper: broadcastStore.currentShowType !== IShowType.PRIVATE,
              isSystemTrigger: false,
              message: item.message!,
              suggestedReply: cleanedReply,
              createdAt: Date.now(),
            };

        if (!isSystemTrigger) {
          const existingCard = this.aiRecommendations.find(
            r =>
              !r.isSystemTrigger &&
              r.targetUsername === recommendation.targetUsername
          );
          if (existingCard) {
            this.clearDismissTimer(existingCard.id);
            this.aiRecommendations = this.aiRecommendations.filter(
              r => r.id !== existingCard.id
            );
          }
        }

        const updated = [...this.aiRecommendations, recommendation];
        if (updated.length > MAX_RECOMMENDATIONS) {
          const removed = updated.shift();
          if (removed) {
            this.clearDismissTimer(removed.id);
          }
        }
        this.aiRecommendations = updated;

        const timer = setTimeout(() => {
          this.dismissRecommendation(id);
        }, AUTO_DISMISS_MS);
        this.dismissTimers.set(id, timer);
      }
    } catch (error: any) {
      this.log("processQueue error", error?.message || error);
    } finally {
      this.isProcessingQueue = false;
      if (this.pendingQueue.length > 0) {
        this.processQueue();
      }
    }
  };

  private recordMessageHistory = (sessionId: string, message: string) => {
    const history = this.messageHistory.get(sessionId) || [];
    history.push(message);
    if (history.length > MAX_HISTORY_MESSAGES) {
      history.shift();
    }
    this.messageHistory.set(sessionId, history);
    this.saveMessageHistory(sessionId);
  };

  private loadMessageHistory = () => {
    try {
      const raw = sessionStorage.getItem(HISTORY_STORAGE_KEY);
      if (!raw) return;
      const parsed: Record<string, { messages: string[]; updatedAt: number }> =
        JSON.parse(raw);
      const now = Date.now();
      let hasExpired = false;
      for (const [sessionId, entry] of Object.entries(parsed)) {
        if (entry.updatedAt && now - entry.updatedAt < HISTORY_EXPIRY_MS) {
          this.messageHistory.set(sessionId, entry.messages);
        } else {
          hasExpired = true;
        }
      }
      if (hasExpired) {
        this.saveMessageHistory();
      }
    } catch {
      // ignore corrupt data
    }
  };

  private saveMessageHistory = (updatedSessionId?: string) => {
    try {
      const stored = sessionStorage.getItem(HISTORY_STORAGE_KEY);
      const existing: Record<
        string,
        { messages: string[]; updatedAt: number }
      > = stored ? JSON.parse(stored) : {};
      const payload: Record<string, { messages: string[]; updatedAt: number }> =
        {};
      for (const [sessionId, messages] of this.messageHistory.entries()) {
        payload[sessionId] = {
          messages,
          updatedAt:
            sessionId === updatedSessionId
              ? Date.now()
              : existing[sessionId]?.updatedAt || Date.now(),
        };
      }
      sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
  };

  useRecommendation = (id: string) => {
    const rec = this.aiRecommendations.find(r => r.id === id);
    if (!rec) return;

    this.aiSentMessages.add(rec.suggestedReply);

    const isPrivateShow = broadcastStore.currentShowType === IShowType.PRIVATE;
    const chatMode = broadcastStore.isInPaidShow
      ? ChatModeEnum.PRIVATE
      : ChatModeEnum.PUBLIC;

    if (rec.isSystemTrigger) {
      nodeChatStore.sendMessage(rec.suggestedReply, chatMode);
    } else if (rec.targetUserId && !isPrivateShow) {
      nodeChatStore.sendMessage(
        rec.suggestedReply,
        ChatModeEnum.WHISPER,
        rec.targetUsername,
        rec.targetUserId
      );
    } else {
      nodeChatStore.sendMessage(rec.suggestedReply, chatMode);
    }

    this.removeRecommendation(id);
  };

  requestRecommendation = (message: IChatMessage) => {
    if (message.isModel || message.isAdmin) return;

    const cleanUsername = this.cleanMemberUsername(message.from);
    const existingCard = this.aiRecommendations.find(
      r => r.targetUsername === cleanUsername
    );
    if (existingCard) {
      this.removeRecommendation(existingCard.id);
    }

    const mp = profileStore.modelProfile;
    const modelName = mp.screen_name || mp.username || "";
    const memberId = (message.userId || message.from || "unknown").split(
      "@"
    )[0];

    this.pendingQueue = [
      ...this.pendingQueue,
      {
        type: "system",
        systemMessage: message.text,
        sessionId: `${memberId}-${modelName}`,
        targetUsername: cleanUsername,
        targetUserId: message.userId,
        chatType: "member",
        memberHandle: message.from,
      },
    ];
    this.processQueue();
  };

  dismissRecommendation = (id: string) => {
    this.removeRecommendation(id);
  };

  private setupSystemTriggers = () => {
    this.memberJoinDisposer = reaction(
      () => nodeChatStore.lastMemberJoinEvent,
      event => {
        if (event) {
          this.onSystemMemberJoined(event.handle, event.username);
        }
      }
    );

    this.showTypeChangeDisposer = reaction(
      () => broadcastStore.currentShowType,
      () => {
        this.memberJoinSuppressed = true;
        if (this.memberJoinSuppressTimer) {
          clearTimeout(this.memberJoinSuppressTimer);
        }
        this.memberJoinSuppressTimer = setTimeout(() => {
          this.memberJoinSuppressed = false;
        }, 5000);
      }
    );

    this.memberLeaveDisposer = reaction(
      () => nodeChatStore.lastMemberLeaveEvent,
      event => {
        if (event) {
          this.onMemberLeft(event.username);
        }
      }
    );

    this.transactionDisposer = reaction(
      () => nodeChatStore.lastTransactionEvent,
      event => {
        if (event) {
          this.onMemberActionTrigger(
            event.handle,
            event.username,
            event.message
          );

          const hadOtherMembers =
            this.transactionTriggerUsernames.size > 0 &&
            !this.transactionTriggerUsernames.has(event.username);
          this.transactionTriggerUsernames.add(event.username);

          if (hadOtherMembers) {
            this.queueGlobalTransactionTrigger(event.message);
          }
        }
      }
    );

    this.favoriteDisposer = reaction(
      () => nodeChatStore.lastFavoriteEvent,
      event => {
        if (event)
          this.onMemberActionTrigger(
            event.username,
            event.username,
            `[SYSTEM] ${event.username} favorited the model`
          );
      }
    );

    this.memberStatusDisposer = reaction(
      () => nodeChatStore.lastMemberStatusEvent,
      event => {
        if (event) {
          const msg =
            event.type === "fan_club"
              ? `[SYSTEM] ${event.username} joined the fan club`
              : `[SYSTEM] ${event.username} is a high spender`;
          this.onMemberActionTrigger(event.username, event.username, msg);
        }
      }
    );

    this.resetQuietRoomTimer();

    this.engageInterval = setInterval(() => {
      this.onEngageAudience();
    }, ENGAGE_INTERVAL_MS);
  };

  private onMemberLeft = (username: string) => {
    const matchingIds = this.aiRecommendations
      .filter(r => r.targetUsername === username)
      .map(r => r.id);

    for (const id of matchingIds) {
      this.removeRecommendation(id);
    }

    this.pendingQueue = this.pendingQueue.filter(
      item => item.targetUsername !== username
    );
  };

  private onSystemMemberJoined = (handle: string, username: string) => {
    if (this.memberJoinSuppressed) return;
    if (this.aiRecommendations.length >= MAX_RECOMMENDATIONS) return;

    const mp = profileStore.modelProfile;
    const modelName = mp.screen_name || mp.username || "";
    const member = nodeChatStore.allMemberHash[handle];

    this.pendingQueue = [
      ...this.pendingQueue,
      {
        type: "system",
        systemMessage: `My name is ${username} and I have joined the room`,
        sessionId: `${username}-${modelName}`,
        targetUsername: username,
        targetUserId: member?.id,
        chatType: "member",
        memberHandle: handle,
      },
    ];
    this.processQueue();
  };

  private onMemberActionTrigger = (
    handle: string,
    username: string,
    systemMessage: string
  ) => {
    if (this.aiRecommendations.length >= MAX_RECOMMENDATIONS) return;

    const mp = profileStore.modelProfile;
    const modelName = mp.screen_name || mp.username || "";
    const member = nodeChatStore.allMemberHash[handle];

    this.pendingQueue = [
      ...this.pendingQueue,
      {
        type: "system",
        systemMessage,
        sessionId: `${username}-${modelName}`,
        targetUsername: username,
        targetUserId: member?.id,
        chatType: "member",
        memberHandle: handle,
      },
    ];
    this.processQueue();
  };

  private queueGlobalTransactionTrigger = (systemMessage: string) => {
    const mp = profileStore.modelProfile;
    const modelName = mp.screen_name || mp.username || "";

    this.pendingQueue = [
      ...this.pendingQueue,
      {
        type: "system",
        systemMessage,
        sessionId: `public-${modelName}`,
        chatType: "global",
      },
    ];
    this.processQueue();
  };

  requestTransactionRecommendation = (
    username: string,
    systemMessage: string
  ) => {
    const existingCard = this.aiRecommendations.find(
      r => r.targetUsername === username
    );
    if (existingCard) {
      this.removeRecommendation(existingCard.id);
    }

    const mp = profileStore.modelProfile;
    const modelName = mp.screen_name || mp.username || "";

    this.pendingQueue = [
      ...this.pendingQueue,
      {
        type: "system",
        systemMessage,
        sessionId: `${username}-${modelName}`,
        targetUsername: username,
        chatType: "member",
      },
    ];
    this.processQueue();
  };

  private onRoomQuiet = () => {
    if (!this.isAllowedForCurrentShow) return;
    if (this.aiRecommendations.length >= MAX_RECOMMENDATIONS) return;

    this.queueSystemTriggersForMembers(
      "[SYSTEM] Room is quiet, engage the audience"
    );
  };

  private onEngageAudience = () => {
    if (!this.isAllowedForCurrentShow) return;
    if (this.aiRecommendations.length >= MAX_RECOMMENDATIONS) return;

    this.queueSystemTriggersForMembers("[SYSTEM] Engage the audience");
  };

  private queueSystemTriggersForMembers = (systemMessage: string) => {
    const mp = profileStore.modelProfile;
    const modelName = mp.screen_name || mp.username || "";
    const availableSlots = MAX_RECOMMENDATIONS - this.aiRecommendations.length;
    if (availableSlots <= 0) return;

    const onlineMembers = Object.values(nodeChatStore.allMemberHash).filter(
      m => m.online && !m.isInvisible
    );

    const isPrivateShow = broadcastStore.currentShowType === IShowType.PRIVATE;
    const items: QueueItem[] = [];

    if (!isPrivateShow) {
      items.push({ type: "system", systemMessage, chatType: "global" });
    }

    const memberSlots = availableSlots - items.length;
    const picked = onlineMembers.slice(0, memberSlots);

    for (const m of picked) {
      items.push({
        type: "system" as const,
        systemMessage,
        sessionId: `${m.username}-${modelName}`,
        targetUsername: m.username,
        chatType: "member" as const,
        targetUserId: m.id,
        memberHandle: m.id,
      });
    }

    this.pendingQueue = [...this.pendingQueue, ...items];
    this.processQueue();
  };

  private resetQuietRoomTimer = () => {
    if (this.quietRoomTimer) {
      clearTimeout(this.quietRoomTimer);
    }
    this.quietRoomTimer = setTimeout(() => {
      this.onRoomQuiet();
    }, QUIET_ROOM_MS);
  };

  private removeRecommendation = (id: string) => {
    this.clearDismissTimer(id);
    this.aiRecommendations = this.aiRecommendations.filter(r => r.id !== id);
  };

  private clearDismissTimer = (id: string) => {
    const timer = this.dismissTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.dismissTimers.delete(id);
    }
  };

  private stripRoleplayText = (text: string): string => {
    if (!text) return "";
    return text.replace(/\*[^*]+\*/g, "").trim();
  };

  private cleanMemberUsername = (from: string): string => {
    if (!from) return "";
    if (from.includes("ffadult") || from.includes("bdsm")) {
      return from.split(/_/)[0];
    }
    return from.split(/\+|@/)[0];
  };

  /**
   * Replaces characters outside the AWS Bedrock Agent allowed set
   * [0-9a-zA-Z.:-] with dots so the full identifier is preserved.
   *
   * @param sessionId - Raw session ID string
   * @returns Sanitized session ID safe for Bedrock Agent API calls
   */
  private sanitizeSessionId = (sessionId: string): string => {
    return sessionId.replace(/[^0-9a-zA-Z._:-]/g, ".");
  };

  private buildModelStats = (): string => {
    const mp = profileStore.modelProfile;
    const parts: string[] = [];
    if (mp.gender) parts.push(`Gender: ${mp.gender}`);
    if (mp.age) parts.push(`Age: ${mp.age}`);
    if (mp.astrological_sign) parts.push(`Sign: ${mp.astrological_sign}`);
    if (mp.sexual_orientation)
      parts.push(`Sexual Preference: ${mp.sexual_orientation}`);
    if (mp.country) parts.push(`Country: ${mp.country}`);
    if (mp.languages && mp.languages.length > 0)
      parts.push(`Language: ${mp.languages.join(", ")}`);
    return parts.join("\n");
  };

  private buildModelAboutMe = (): string => {
    const mp = profileStore.modelProfile;
    const extra = mp as any;
    const parts: string[] = [];
    if (mp.gender) parts.push(`Gender: ${mp.gender}`);
    if (mp.age) parts.push(`Age: ${mp.age}`);
    if (mp.public_birthdate) parts.push(`Birthday: ${mp.public_birthdate}`);
    if (mp.astrological_sign) parts.push(`Sign: ${mp.astrological_sign}`);
    if (mp.sexual_orientation)
      parts.push(`Sexual Preference: ${mp.sexual_orientation}`);
    if (mp.marital_status) parts.push(`Marital Status: ${mp.marital_status}`);
    if (mp.country) parts.push(`Country: ${mp.country}`);
    if (mp.languages && mp.languages.length > 0)
      parts.push(`Language: ${mp.languages.join(", ")}`);
    const funFact = mp.fun_fact || mp.pending_fun_fact;
    if (funFact) parts.push(`About Me: ${funFact}`);
    if (extra.describe_yourself)
      parts.push(`Description: ${extra.describe_yourself}`);
    return parts.join("\n");
  };

  private buildModelAttributes = (): string => {
    const mp = profileStore.modelProfile;
    const extra = mp as any;
    const parts: string[] = [];
    if (mp.body_type) parts.push(`Body Type: ${mp.body_type}`);
    if (mp.height && mp.height !== 0) parts.push(`Height: ${mp.height}`);
    if (extra.weight && extra.weight !== "00" && extra.weight !== "0")
      parts.push(`Weight: ${extra.weight}`);
    if (mp.breast_size) parts.push(`Bra Size: ${mp.breast_size}`);
    if (mp.penis_size) parts.push(`Penis Size: ${mp.penis_size}`);
    if (mp.ethnicity) parts.push(`Ethnicity: ${mp.ethnicity}`);
    if (mp.eye_color) parts.push(`Eye Color: ${mp.eye_color}`);
    if (mp.hair_color) parts.push(`Hair Color: ${mp.hair_color}`);
    if (mp.attributes && mp.attributes.length > 0) {
      parts.push(
        `Kinky Attributes: ${mp.attributes.map(a => a.name).join(", ")}`
      );
    }
    return parts.join("\n");
  };

  private buildModelProfileFields = (): Pick<
    AiChatRequest,
    | "model_about_me"
    | "model_stats"
    | "model_attributes"
    | "model_turn_ons"
    | "model_turn_offs"
    | "model_interests"
  > => {
    const mp = profileStore.modelProfile;
    const extra = mp as any;
    return {
      model_about_me: this.buildModelAboutMe(),
      model_stats: this.buildModelStats(),
      model_attributes: this.buildModelAttributes(),
      model_turn_ons:
        extra.turn_ons ||
        (mp.kinks && mp.kinks.length > 0
          ? mp.kinks.map(k => k.name).join(", ")
          : ""),
      model_turn_offs: extra.turn_offs || "",
      model_interests:
        mp.specialities && mp.specialities.length > 0
          ? mp.specialities.map(s => s.name).join(", ")
          : "",
    };
  };

  private buildRequestPayload = (message: IChatMessage): AiChatRequest => {
    const mp = profileStore.modelProfile;
    const rawMemberId = message.userId || message.from || "unknown";
    const memberId = rawMemberId.split("@")[0];
    const modelName = mp.screen_name || mp.username || "";

    return {
      session_id: this.sanitizeSessionId(`${memberId}-${modelName}`),
      message: message.text,
      chat_type: "member",
      model_name: modelName,
      ...this.buildModelProfileFields(),
    };
  };

  private buildSystemPayload = (
    systemMessage: string,
    sessionId?: string,
    chatType?: "global" | "member"
  ): AiChatRequest => {
    const mp = profileStore.modelProfile;
    const modelName = mp.screen_name || mp.username || "";

    return {
      session_id: this.sanitizeSessionId(sessionId || `public-${modelName}`),
      message: systemMessage,
      chat_type: chatType || (sessionId ? "member" : "global"),
      model_name: modelName,
      ...this.buildModelProfileFields(),
    };
  };
}
